import React, { PureComponent } from 'react';
import {
  Text,
  View,
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ActivityIndicator,
  Image,
  ViewStyle,
  TextStyle,
  ImageStyle,
} from 'react-native';
import {
  Icon,
  Theme,
  ActionPanel,
  FormError,
  Sizes,
} from 'fitbeat.react-native-components';
import { UserServiceClient } from 'fitbeat.api-clients';
import { BaseUserProfile } from 'fitbeat.models';
import ImageUploader from 'react-images-upload';
import Avatar from './Avatar';
import { MemoryImageCache } from '../services';
import Button, { ButtonTypes } from './Button';
import { getApiConfig } from '../config';
import { defaultScale } from '../utils';

interface IProps {
  size: number;
  userProfile: BaseUserProfile;
  navigation: any;
}

interface IState {
  isModalVisible: boolean;
  isLoading: boolean;
  error: Error | null;
  forceUpdate: boolean;
  picture: {
    pictureInfo: File;
    base64Image: string;
    previewUrl: string;
  } | null;
  pictureSelected: boolean;
  base64Image?: string;
  avatarError?: Error;
}

export class ChangeableAvatar extends PureComponent<IProps, IState> {
  private userProfileClient: UserServiceClient;

  constructor(props: IProps) {
    super(props);
    const apiConfig = getApiConfig();
    this.userProfileClient = new UserServiceClient(apiConfig, fetch, console.log, undefined);
    this.state = {
      isModalVisible: false,
      isLoading: false,
      error: null,
      forceUpdate: false,
      picture: null,
      pictureSelected: false,
    };

    this.onDrop = this.onDrop.bind(this);
  }

  public showAvatarModal = async (): Promise<void> => {
    try {
      this.setState({
        isLoading: true,
      });
      this.setState({ isModalVisible: true, error: null });
    } catch (error) {
      this.setState({
        error: error as Error,
      });
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  public handleImageResponse = async (response: {
    base64: string;
    cancelled: boolean;
    error: boolean;
  }): Promise<void> => {
    this.setState({
      error: null,
      isLoading: true,
    });
    try {
      if (response.cancelled) {
        this.hideAvatarModal();
        return;
      }

      if (response.error) {
        this.setState({
          avatarError: new Error('Image upload failed'),
        });
        return;
      }
      await this.userProfileClient.addUserBase64Avatar(response.base64);
      MemoryImageCache.remove(this.props.userProfile.uid);
      this.hideAvatarModal();
      this.setState({
        forceUpdate: true,
        base64Image: response.base64,
        picture: null,
        pictureSelected: false,
      });
      window.location.href = '/profile';
    } catch (error) {
      this.setState({
        error: error as Error,
      });
    } finally {
      this.setState({
        isLoading: false,
      });
    }
  };

  public onDrop = (picture: File[]): void => {
    if (picture.length === 0) {
      return;
    }

    const file = picture[0];
    const reader = new FileReader();

    reader.onload = () => {
      const base64Image = reader.result as string;
      this.setState({
        picture: {
          pictureInfo: file,
          base64Image: base64Image.split(';base64,')[1],
          previewUrl: base64Image,
        },
        pictureSelected: true,
      });
    };
    reader.readAsDataURL(file);
  };

  public webUpload = (): void => {
    if (this.state.picture) {
      this.handleImageResponse({
        base64: this.state.picture.base64Image,
        cancelled: false,
        error: false,
      }).catch((error) => {
        this.setState({
          error: error as Error,
        });
      });
    }
  };

  public hideAvatarModal = (): void => {
    this.setState({ isModalVisible: false });
  };

  public render() {
    const {
      isModalVisible, error, isLoading, forceUpdate, base64Image,
    } = this.state;
    return (
      <View>
        <TouchableWithoutFeedback
          style={{ padding: 20 }}
          onPress={this.showAvatarModal}
        >
          <View>
            <Avatar
              size={this.props.size}
              avatarId={this.props.userProfile.uid}
              iconColor={Theme.GREY_MEDIUM}
              forceUpdate={forceUpdate}
              base64Image={base64Image}
              navigation={this.props.navigation}
            />
          </View>
        </TouchableWithoutFeedback>
        <Text style={[styles.saveButtonContainer, { color: Theme.PRIMARY }]} onPress={this.showAvatarModal}>
          Update Image
        </Text>
        <ActionPanel
          size='small'
          forcePopup
          transparent
          animationType='fade'
          onRequestClose={this.hideAvatarModal}
          visible={isModalVisible}
        >
          <View style={styles.modalContainer}>
            <View style={styles.innerContainer}>
              {isLoading && (
                <ActivityIndicator
                  size='small'
                  color={Theme.PRIMARY}
                  animating={isLoading}
                  style={styles.activityIndicator}
                />
              )}
              {!isLoading && (
                <View>
                  <TouchableOpacity
                    style={{ alignSelf: 'flex-end' }}
                    onPress={this.hideAvatarModal}
                  >
                    <Icon
                      size={35}
                      name='md-close'
                      color={Theme.GREY}
                    />
                  </TouchableOpacity>
                  {Platform.OS === 'web' && (
                    <View>
                      <Text style={styles.modalText}>
                        Capture or upload a selfie image (just your face please)
                      </Text>
                      <View>
                        <FormError error={error} color={Theme.BLACK} />
                        <View style={styles.WebButtonContainer}>
                          {this.state.pictureSelected && (
                            <View style={styles.previewContainer}>
                              <Image
                                source={{ uri: this.state.picture?.previewUrl }}
                                style={styles.previewImage as ImageStyle}
                                resizeMode='cover'
                              />
                            </View>
                          )}
                          <ImageUploader
                            withIcon={false}
                            withPreview={false}
                            fileContainerStyle={{
                              boxShadow: 'none',
                              paddingTop: 5,
                            }}
                            singleImage
                            buttonText='Choose Image'
                            buttonStyles={{
                              height: Sizes.ButtonHeight,
                              borderWidth: 1,
                              borderRadius: 2,
                              alignItems: 'center',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              backgroundColor: Theme.PRIMARY,
                              fontSize: 16,
                              fontFamily: Theme.CustomBold,
                              textAlign: 'center',
                            }}
                            onChange={this.onDrop}
                            imgExtension={['.jpg', '.jpeg', '.png']}
                            maxFileSize={300000}
                            withLabel
                            label={'Max file size: 300kb, Selected image: '
                              + `${this.state.picture?.pictureInfo?.name || ''}`}
                            labelStyles={{ fontFamily: Theme.Custom500 }}
                          />
                          <Button
                            disabled={!this.state.pictureSelected}
                            containerStyle={styles.uploadButton}
                            onPress={this.webUpload}
                            title='Upload'
                            buttonType={ButtonTypes.NORMAL}
                            fontColor={Theme.WHITE}
                          />
                        </View>
                      </View>
                    </View>
                  )}
                </View>
              )}
            </View>
          </View>
        </ActionPanel>
      </View>
    );
  }
}

interface IStyles {
  saveButtonContainer: TextStyle;
  modalContainer: ViewStyle;
  innerContainer: ViewStyle;
  modalText: TextStyle;
  WebButtonContainer: ViewStyle;
  uploadButton: ViewStyle;
  activityIndicator: ViewStyle;
  previewContainer: ViewStyle;
  previewImage: ImageStyle;
}

const styles = StyleSheet.create<IStyles>({
  saveButtonContainer: {
    fontSize: 14,
    textAlign: 'center',
  },
  modalContainer: {
    flex: 1,
  },
  innerContainer: {
    margin: 15,
    padding: 10,
    marginTop: 70,
    borderRadius: 10,
    paddingBottom: 30,
    backgroundColor: Theme.WHITE,
  },
  modalText: {
    fontSize: 20,
    lineHeight: 25,
    fontFamily: Theme.Custom500,
    textAlign: 'center',
    paddingVertical: 15,
    color: Theme.GREY_DARK,
  },
  WebButtonContainer: {
    borderTopWidth: 1,
    flexDirection: 'column',
    borderTopColor: Theme.WHITE,
  },
  uploadButton: {
    flex: 1,
  },
  activityIndicator: {
    paddingVertical: 10,
  },
  previewContainer: {
    alignItems: 'center',
  },
  previewImage: {
    width: defaultScale(150),
    height: defaultScale(150),
    borderRadius: defaultScale(75),
  },
});
